// frontend/src/pages/RequisitionsList.jsx
import React, { useEffect, useState, useContext, useRef } from 'react';
import apiClient from '../api/apiClient';
import { AuthContext } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PDFDocument } from 'pdf-lib';

import {
  canInlineChangeRequisitionStatus,
  hasSuperPlusUltraAccess,
} from '../utils/userPermissions';

import { EDIT_STATUS_OPTIONS } from '../components/Requisitions/edit/constants';
import {
  displayStatus,
  isNoAplicaLabel,
} from '../components/Requisitions/edit/utils';

const API_BASE = (process.env.REACT_APP_API_URL || '/api/').replace(/\/?$/, '/');

function getStatusOptionsForRow(status) {
  const currentValue = String(status || '').trim().toLowerCase();

  if (!currentValue) return EDIT_STATUS_OPTIONS;

  const exists = EDIT_STATUS_OPTIONS.some(
    (opt) => String(opt.value).trim().toLowerCase() === currentValue
  );

  if (exists) return EDIT_STATUS_OPTIONS;

  return [
    { value: currentValue, label: displayStatus(currentValue) },
    ...EDIT_STATUS_OPTIONS,
  ];
}

function normalizeStatus(value) {
  return String(value || '').trim().toLowerCase();
}

function textOrDash(value) {
  const normalized = String(value || '').trim();
  return normalized || '—';
}

function extractApiErrorMessage(err, fallback = 'No se pudo cambiar el estatus.') {
  const data = err?.response?.data;

  if (typeof data === 'string' && data.trim()) return data;
  if (data?.detail) return data.detail;

  if (Array.isArray(data?.status) && data.status.length > 0) {
    return data.status.join(' ');
  }

  if (typeof data === 'object' && data) {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return fallback;
    }
  }

  return err?.message || fallback;
}

async function readErrorMessage(res) {
  const ct = (res.headers.get('content-type') || '').toLowerCase();

  if (ct.includes('application/json')) {
    try {
      const data = await res.json();
      if (typeof data === 'string') return data;
      if (data?.detail) return data.detail;
      return JSON.stringify(data, null, 2);
    } catch (e) {
      // cae a texto
    }
  }

  try {
    const text = await res.text();
    return text || 'Error desconocido (sin body).';
  } catch (e) {
    return 'Error desconocido (no se pudo leer la respuesta).';
  }
}

// Construye URL del endpoint PDF.
// include_quotes=0 => solo requisición (para Imprimir)
// include_quotes=1 o sin parámetro => comportamiento normal (para PDF)
function buildExportPdfUrl(id, { includeQuotes = true } = {}) {
  const params = new URLSearchParams();

  if (!includeQuotes) {
    params.set('include_quotes', '0');
  }

  const qs = params.toString();
  return `${API_BASE}requisitions/${id}/export_pdf/${qs ? `?${qs}` : ''}`;
}

function buildFormalPdfUrl(id) {
  return `${API_BASE}requisitions/${id}/export_formal_pdf/`;
}

// Duplica páginas: 1,1,2,2,3,3...
async function duplicatePdfPages(pdfBytes) {
  const original = await PDFDocument.load(pdfBytes);
  const out = await PDFDocument.create();

  const indices = original.getPageIndices();
  for (const idx of indices) {
    const [p1] = await out.copyPages(original, [idx]);
    out.addPage(p1);
    const [p2] = await out.copyPages(original, [idx]);
    out.addPage(p2);
  }

  return await out.save();
}

function openPrintPopup() {
  const popup = window.open('', '_blank');
  if (!popup) return null;

  try {
    popup.document.write(`
      <!doctype html>
      <html lang="es">
        <head>
          <meta charset="utf-8" />
          <title>Preparando impresion...</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 24px;
              line-height: 1.5;
            }
          </style>
        </head>
        <body>
          <h1>Preparando impresion...</h1>
          <p>Si el dialogo no aparece automaticamente, usa Ctrl+P desde esta pestana.</p>
        </body>
      </html>
    `);
    popup.document.close();
  } catch (e) {
    // Si el navegador limita document.write aqui, seguimos con la ventana abierta.
  }

  return popup;
}

function closePrintPopup(popup) {
  if (!popup) return;

  try {
    if (!popup.closed) popup.close();
  } catch (e) {
    // No pasa nada si el navegador ya la cerro o bloqueo acceso.
  }
}

function scheduleBlobUrlCleanup(url, delayMs = 60000) {
  window.setTimeout(() => {
    try {
      URL.revokeObjectURL(url);
    } catch (e) {
      // Ignora fallas de cleanup.
    }
  }, delayMs);
}

function tryOpenPdfInPopup(url, popup) {
  if (!popup || popup.closed) return false;

  try {
    popup.location.replace(url);

    window.setTimeout(() => {
      try {
        popup.focus();
      } catch (e) {
        // Algunos navegadores bloquean focus() sobre el visor PDF.
      }

      try {
        popup.print();
      } catch (e) {
        // Si auto-print falla, el PDF queda abierto para imprimir manualmente.
      }
    }, 1200);

    return true;
  } catch (e) {
    return false;
  }
}

// Imprime un PDF (bytes). Usa una ventana abierta desde el click para evitar
// errores de permisos/origen cruzado del visor PDF embebido en iframes.
async function printPdfBytes(pdfBytes, { popup = null } = {}) {
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  scheduleBlobUrlCleanup(url);

  if (tryOpenPdfInPopup(url, popup)) {
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.src = url;

  document.body.appendChild(iframe);

  const cleanup = () => {
    try {
      document.body.removeChild(iframe);
    } catch {}
  };

  iframe.onload = () => {
    setTimeout(() => {
      try {
        const w = iframe.contentWindow;
        if (!w) {
          throw new Error('No se pudo acceder a la ventana de impresion.');
        }

        w.focus();
        w.print();
      } catch (e) {
        const fallbackLink = document.createElement('a');
        fallbackLink.href = url;
        fallbackLink.target = '_blank';
        fallbackLink.rel = 'noopener noreferrer';
        fallbackLink.click();
      }
    }, 250);

    setTimeout(() => cleanup(), 15000);
  };
}

export default function RequisitionsList() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [requisitions, setRequisitions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLegacy, setShowLegacy] = useState(true);
  const [showNew, setShowNew] = useState(true);

  // ✅ Paginación (server-side)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const [loading, setLoading] = useState(false);
  const [busyStatusById, setBusyStatusById] = useState({});
  const fetchRequestIdRef = useRef(0);

  // ✅ Permisos alineados a la nueva lógica
  const canChangeStatusInline = canInlineChangeRequisitionStatus(user);
  const canViewAdministrativeUnitColumn = hasSuperPlusUltraAccess(user);
  const showModifyColumn = requisitions.some((req) => !!req?.can_edit_requisition);
  const showDraftEditableNote = !canChangeStatusInline;

  const userName = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim();

  // ✅ helper para badge de tipo (Legacy / Nueva)
  const getReqTypeMeta = (row) => {
    const type = row?.requisition_type === 'legacy' ? 'legacy' : 'new';
    const label =
      row?.requisition_type_label ||
      (type === 'legacy' ? 'Legacy' : 'Nueva');

    const className =
      type === 'legacy'
        ? 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300'
        : 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-cyan-50 text-teal-700 border border-teal-200';

    return { type, label, className };
  };

  const fetchRequisitions = async () => {
    const requestId = fetchRequestIdRef.current + 1;
    fetchRequestIdRef.current = requestId;

    try {
      setLoading(true);

      const params = new URLSearchParams();

      // ✅ orden
      params.set('ordering', '-created_at,-id');

      // ✅ paginación
      params.set('page', String(page));
      params.set('page_size', String(pageSize));

      // ✅ búsqueda global
      const q = (searchTerm || '').trim();
      if (q) params.set('search', q);
      params.set('include_legacy', showLegacy ? '1' : '0');
      params.set('include_new', showNew ? '1' : '0');

      const response = await apiClient.get(`/requisitions/?${params.toString()}`);

      const data = response.data;
      const rows = Array.isArray(data) ? data : (data.results || []);

      if (requestId !== fetchRequestIdRef.current) {
        return;
      }

      setRequisitions(rows);

      if (!Array.isArray(data)) {
        setTotalCount(data.count ?? rows.length);
        setHasNext(Boolean(data.next));
        setHasPrev(Boolean(data.previous));
      } else {
        setTotalCount(rows.length);
        setHasNext(false);
        setHasPrev(false);
      }
    } catch (err) {
      console.error(err);
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      setRequisitions([]);
      setTotalCount(0);
      setHasNext(false);
      setHasPrev(false);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchRequisitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchTerm, showLegacy, showNew]);

  const handleChangeStatus = async (req, newStatus) => {
    const reqId = req?.id;
    const nextStatus = String(newStatus || '').trim().toLowerCase();
    const currentStatus = String(req?.status || '').trim().toLowerCase();

    if (!reqId || !nextStatus || nextStatus === currentStatus) return;

    setBusyStatusById((prev) => ({
      ...prev,
      [reqId]: true,
    }));

    try {
      const response = await apiClient.patch(`/requisitions/${reqId}/change_status/`, {
        status: nextStatus,
      });

      const updated = response?.data || {};

      setRequisitions((prev) =>
        prev.map((row) =>
          Number(row.id) === Number(reqId)
            ? {
                ...row,
                ...updated,
                status: updated.status || nextStatus,
                status_history: Array.isArray(updated.status_history)
                  ? updated.status_history
                  : row.status_history,
              }
            : row
        )
      );
    } catch (err) {
      console.error(err);
      alert(extractApiErrorMessage(err));
    } finally {
      setBusyStatusById((prev) => {
        const next = { ...prev };
        delete next[reqId];
        return next;
      });
    }
  };

  const ModifyButton = ({ id }) => {
    const goEdit = (e) => {
      e.preventDefault();
      e.stopPropagation();
      navigate(`/requisitions/edit/${id}`);
    };

    return (
      <button
        type="button"
        onClick={goEdit}
        className="inline-flex items-center px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700"
      >
        Modificar
      </button>
    );
  };

  const handleDownloadPDF = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No se encontró token. Inicia sesión de nuevo.');
        return;
      }

      const url = buildExportPdfUrl(id, { includeQuotes: true });

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/pdf,application/json;q=0.9,*/*;q=0.8',
        },
      });

      if (!res.ok) {
        const msg = await readErrorMessage(res);
        console.error('PDF error:', res.status, msg);
        alert(`Error ${res.status}:\n${msg}`);
        return;
      }

      const blob = await res.blob();
      const fileURL = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = fileURL;
      a.download = `requisicion_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(fileURL);
    } catch (err) {
      console.error(err);
      alert('No se pudo generar el PDF (error inesperado).');
    }
  };

  const handlePrintDuplicatedPDF = async (id) => {
    let printPopup = null;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No se encontro token. Inicia sesion de nuevo.');
        return;
      }

      printPopup = openPrintPopup();
      const url = buildExportPdfUrl(id, { includeQuotes: false });

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/pdf,application/json;q=0.9,*/*;q=0.8',
        },
      });

      if (!res.ok) {
        closePrintPopup(printPopup);
        const msg = await readErrorMessage(res);
        console.error('PDF error:', res.status, msg);
        alert(`Error ${res.status}:\n${msg}`);
        return;
      }

      const originalBytes = await res.arrayBuffer();
      const duplicatedBytes = await duplicatePdfPages(originalBytes);

      await printPdfBytes(duplicatedBytes, { popup: printPopup });
    } catch (err) {
      closePrintPopup(printPopup);
      console.error(err);
      alert('No se pudo imprimir el PDF (error inesperado).');
    }
  };

  const handlePrintFormalPDF = async (id) => {
    let printPopup = null;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No se encontro token. Inicia sesion de nuevo.');
        return;
      }

      printPopup = openPrintPopup();
      const res = await fetch(buildFormalPdfUrl(id), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/pdf,application/json;q=0.9,*/*;q=0.8',
        },
      });

      if (!res.ok) {
        closePrintPopup(printPopup);
        const msg = await readErrorMessage(res);
        console.error('Formato formal error:', res.status, msg);
        alert(`Error ${res.status}:\n${msg}`);
        return;
      }

      const originalBytes = await res.arrayBuffer();
      const duplicatedBytes = await duplicatePdfPages(originalBytes);

      await printPdfBytes(duplicatedBytes, { popup: printPopup });
    } catch (err) {
      closePrintPopup(printPopup);
      console.error(err);
      alert('No se pudo imprimir el Formato Formal (error inesperado).');
    }
  };

  const start = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = totalCount === 0 ? 0 : Math.min(totalCount, (page - 1) * pageSize + requisitions.length);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setPage(1);
  };

  const totalColumns =
    15 + (canViewAdministrativeUnitColumn ? 1 : 0) + (showModifyColumn ? 1 : 0);

  return (
    <div className="flex flex-col">
      <h1 className="text-xl font-semibold mb-2">
        Favor de imprimir, firmar y entregar su requisición en Fecha y Hora establecidas en Secretaría Administrativa
      </h1>

      <p className="mb-2">
        Listado de Requisición del Usuario : <span className="font-bold">{userName}</span>
      </p>

      {showDraftEditableNote && (
        <p className="text-red-600 font-bold mb-4">
          * Nota: Solo las requisiciones en borrador pueden ser modificadas por el usuario.
        </p>
      )}

      <div className="flex items-center gap-3 my-3">
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Buscar por folio, fecha, tipo, autor, motivo, estatus, proyecto, fuente, convenio..."
          className="w-[420px] px-3 py-2 border rounded"
        />

        {searchTerm && (
          <button onClick={clearSearch} className="px-3 py-2 border rounded bg-white" type="button">
            Limpiar
          </button>
        )}

        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showLegacy}
            onChange={(e) => {
              setShowLegacy(e.target.checked);
              setPage(1);
            }}
          />
          Mostrar Legacy
        </label>

        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showNew}
            onChange={(e) => {
              setShowNew(e.target.checked);
              setPage(1);
            }}
          />
          Mostrar Nuevas
        </label>

        <div className="ml-auto text-sm opacity-70">
          {loading ? 'Cargando...' : `Mostrando ${start}–${end} de ${totalCount}`}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300">
          <thead className="bg-indigo-100">
            <tr>
              <th className="px-4 py-2 border">Folio</th>
              <th className="px-4 py-2 border">Fecha</th>
              <th className="px-4 py-2 border">Tipo</th>
              {canViewAdministrativeUnitColumn && <th className="px-4 py-2 border">Unidad Administrativa</th>}
              <th className="px-4 py-2 border">Autor</th>
              <th className="px-4 py-2 border">Departamento Solicitante</th>
              <th className="px-4 py-2 border">Servicio Externo / Académico</th>
              <th className="px-4 py-2 border">Proyecto</th>
              <th className="px-4 py-2 border">Licitación</th>
              <th className="px-4 py-2 border">Fuente de Financiamiento</th>
              <th className="px-4 py-2 border">Unidad Presupuestal</th>
              <th className="px-4 py-2 border">Convenios</th>
              <th className="px-4 py-2 border">Motivo</th>
              <th className="px-4 py-2 border">Costo Total</th>
              <th className="px-4 py-2 border">Estatus</th>
              <th className="px-4 py-2 border">Exportar</th>
              {showModifyColumn && <th className="px-4 py-2 border">Modificar</th>}
            </tr>
          </thead>

          <tbody>
            {requisitions.length === 0 ? (
              <tr>
                <td colSpan={totalColumns} className="text-center py-4 border-2 border-indigo-300">
                  {loading ? 'Cargando...' : (searchTerm ? 'No hay coincidencias' : 'No hay requisiciones')}
                </td>
              </tr>
            ) : (
              requisitions.map((req) => {
                const tenderText =
                  req?.tender_label ||
                  req?.tender_display ||
                  req?.tender_name ||
                  req?.tender?.label ||
                  req?.tender?.name ||
                  req?.tender?.description ||
                  '';

                const requiresAckForPdf = isNoAplicaLabel(tenderText);
                const isDraft = normalizeStatus(req?.status) === 'draft';
                const canExport = !isDraft && (requiresAckForPdf ? req?.ack_cost_realistic === true : true);
                const canExportFormal = Boolean(req?.can_export_formal_pdf);
                const canChangeRowStatus = canChangeStatusInline && Boolean(req?.can_change_requisition_status);

                const typeMeta = getReqTypeMeta(req);
                const statusOptions = getStatusOptionsForRow(req?.status);
                const statusBusy = Boolean(busyStatusById?.[req.id]);

                return (
                  <tr key={req.id} className="even:bg-gray-50 hover:bg-indigo-50 transition-colors">
                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {req.folio ?? req.id}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {req.created_at ? new Date(req.created_at).toLocaleDateString('es-MX') : ''}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      <span className={typeMeta.className} title={typeMeta.label}>
                        {typeMeta.label}
                      </span>
                    </td>

                    {canViewAdministrativeUnitColumn && (
                      <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                        {textOrDash(req.administrative_unit_label)}
                      </td>
                    )}

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {textOrDash(req.author_display)}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {textOrDash(req.requesting_department_label)}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {textOrDash(req.external_service_label)}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {textOrDash(req.project_label)}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {textOrDash(req.tender_label)}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {textOrDash(req.funding_source_label)}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {textOrDash(req.budget_unit_label)}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {textOrDash(req.agreement_label)}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {textOrDash(req.requisition_reason)}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {textOrDash(req.total_cost_display)}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      {canChangeRowStatus ? (
                        <div className="flex flex-col items-center">
                          <select
                            value={String(req?.status || 'pending').trim().toLowerCase()}
                            onChange={(e) => handleChangeStatus(req, e.target.value)}
                            disabled={statusBusy}
                            className="min-w-[220px] border rounded px-2 py-1 bg-white disabled:bg-gray-100 disabled:text-gray-500"
                          >
                            {statusOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>

                          {statusBusy && (
                            <div className="text-xs text-gray-500 mt-1">Guardando...</div>
                          )}
                        </div>
                      ) : (
                        req.status_label || displayStatus(req.status)
                      )}
                    </td>

                    <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          disabled={!canExport}
                          className={`px-2 py-1 rounded text-white ${
                            canExport ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-400 cursor-not-allowed'
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!canExport) return;
                            handleDownloadPDF(req.id);
                          }}
                          title={
                            canExport
                              ? 'Descargar PDF'
                              : isDraft
                                ? 'Las requisiciones en borrador no se pueden exportar ni imprimir'
                                : 'Primero confirma: costo aproximado realista (en Modificar)'
                          }
                          type="button"
                        >
                          PDF
                        </button>

                        <button
                          disabled={!canExport}
                          className={`px-2 py-1 rounded text-white ${
                            canExport ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-400 cursor-not-allowed'
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!canExport) return;
                            handlePrintDuplicatedPDF(req.id);
                          }}
                          title={
                            canExport
                              ? 'Imprimir 2 copias (páginas duplicadas, sin cotizaciones)'
                              : isDraft
                                ? 'Las requisiciones en borrador no se pueden exportar ni imprimir'
                                : 'Primero confirma: costo aproximado realista (en Modificar)'
                          }
                          type="button"
                        >
                          Imprimir
                        </button>

                        {canExportFormal && (
                          <button
                            className="px-2 py-1 rounded text-white bg-amber-600 hover:bg-amber-700"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handlePrintFormalPDF(req.id);
                            }}
                            title="Imprimir Formato Formal (2 copias, páginas duplicadas)"
                            type="button"
                          >
                            Formato Formal
                          </button>
                        )}
                      </div>

                      {!canExport && !isDraft && requiresAckForPdf && (
                        <div className="text-xs text-red-600 mt-1">
                          Confirma “costo aproximado realista” en Modificar.
                        </div>
                      )}
                    </td>

                    {showModifyColumn && (
                      <td className="px-4 py-2 border-2 border-indigo-300 text-center">
                        {req?.can_edit_requisition ? <ModifyButton id={req.id} /> : null}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">
          {loading ? 'Cargando...' : `Mostrando ${start}–${end} de ${totalCount}`}
        </div>

        <div className="flex items-center gap-2">
          <button
            className="px-3 py-2 rounded border disabled:opacity-50"
            disabled={loading || !hasPrev || page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            type="button"
          >
            Anterior
          </button>

          <span className="text-sm">Página {page}</span>

          <button
            className="px-3 py-2 rounded border disabled:opacity-50"
            disabled={loading || !hasNext}
            onClick={() => setPage((p) => p + 1)}
            type="button"
          >
            Siguiente
          </button>

          <select
            className="ml-2 px-2 py-2 rounded border text-sm"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            disabled={loading}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
    </div>
  );
}
